import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MGender from "@/components/ui/molecules/MGender";
import { genderOptions } from "@/types/member";
import { IconCalendar } from "@tabler/icons-react";
import { getMemberInfo } from "@/hooks/useMembers";
import { membersService } from "@/services/members.service";
import MToast from "@/components/ui/atoms/MToast";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { MButton } from "@/components/ui/atoms/MButtons";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";

const AddPetProfile: React.FC = () => {
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const { managedMembers } = useManagedMembersContext();

  // Read pet fields from localStorage
  const petFields = (() => {
    const saved = localStorage.getItem("add_member_form");
    if (!saved) return { petType: "", breed: "", gender: "" };
    const obj = JSON.parse(saved);
    return {
      petType: obj.petType || "",
      breed: obj.breed || "",
      gender: obj.gender || "",
    };
  })();

  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [name, setName] = useState("");
  const [dob, setDob] = useState<{ month: number; year: number } | undefined>();
  const [nickname, setNickname] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).nickname || "" : "";
  });
  const saveToLocalStorage = (data: any) => {
    const saved = localStorage.getItem("add_member_form");
    const form = saved ? JSON.parse(saved) : {};
    localStorage.setItem(
      "add_member_form",
      JSON.stringify({ ...form, ...data })
    );
  };
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    variant: "success" | "error";
  }>({ open: false, message: "", variant: "success" });

  // Helper for DOB display
  const dobDisplay = dob
    ? (() => {
        const date = new Date(dob.year, dob.month - 1);
        return date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      })()
    : "";

  // All fields required except nickname
  const allFilled =
    name && dob && petFields.gender && petFields.petType && petFields.breed;

  // Add member service call (like Consent)
  const handleNext = async () => {
    if (!allFilled) return;
    setSaving(true);
    setToast({ open: false, message: "", variant: "success" });

    // Check if this is the first family member BEFORE adding
    const isFirstFamilyMember = managedMembers.length === 1;

    try {
      const familyId = memberInfo.current_family || memberInfo.familyId || null;
      const payload: any = {
        firstName: name.trim(),
        dobMonth: dob?.month,
        dobYear: dob?.year,
        gender:
          petFields.gender === "male"
            ? 1
            : petFields.gender === "female"
            ? 2
            : 3,
        relationship: 6, // Pet
        family_id: familyId,
        current_family: familyId,
        member_type: "p",
        nickname: nickname.trim(),
        other_attrs: {
          petType: petFields.petType,
          breed: petFields.breed,
        },
      };
      await membersService.createMember(payload);
      localStorage.removeItem("add_member_form");
      navigate("/profile/family/add-member/success", {
        state: {
          memberName: name,
          nickname: nickname,
          isChildOrPet: true,
          isFirstFamilyMember,
        },
      });
    } catch (err: any) {
      let errorMsg = "Failed to add pet.";
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setToast({ open: true, message: errorMsg, variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title="Set up their profile"
        description="Add details to create their profile."
      />
      <MSpacer mt={34} />
      <div className="flex flex-col gap-[32px] flex-1">
        <MInputField
          label="What's their full name?"
          showLabel
          placeholder="Not Answered"
          variant="Underlined"
          size="md"
          value={name}
          onChange={(e) => {
            const val = e.target.value.trimStart();
            setName(val);
            saveToLocalStorage({ firstName: val.trim() });
          }}
          mandatory
        />
        <MInputField
          label="What's their month & year of birth?"
          showLabel
          placeholder="Not Answered"
          variant="Underlined"
          size="md"
          value={dobDisplay}
          onClick={() => setDobPickerOpen(true)}
          readOnly
          icon={<IconCalendar />}
          mandatory
        />
        <MDatePicker
          open={dobPickerOpen}
          onClose={() => setDobPickerOpen(false)}
          onDone={(date) => {
            setDob({ month: date.getMonth() + 1, year: date.getFullYear() });
            setDobPickerOpen(false);
          }}
          initialValue={dob ? new Date(dob.year, dob.month - 1) : undefined}
          title="Select Month & Year"
          hideDate
        />
        <MInputField
          label="Tell us their nickname? (Optional)"
          showLabel
          placeholder="Not Answered"
          variant="Underlined"
          size="md"
          value={nickname}
          onChange={(e) => {
            const val = e.target.value.trimStart();
            setNickname(val);
            saveToLocalStorage({ nickname: val.trim() });
          }}
        />
      </div>
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          className="w-full"
          disabled={!allFilled || saving}
          onClick={handleNext}
        >
          {saving ? "Saving..." : "Next"}
        </MButton>
      </div>
      {toast.open && <MToast message={toast.message} variant={toast.variant} />}
    </div>
  );
};

export default AddPetProfile;
